/**
 * <mejoris-carousel> — Carrusel oficial del Sistema Mejoris.
 * Componente reutilizable para cualquier landing (galerías, testimonios,
 * beneficios, antes/después, productos) — NO usa el slider nativo de Horizon.
 *
 * Uso:
 *   <mejoris-carousel class="mc" data-speed="36" data-gap="16">
 *     <div class="mc__track">
 *       <div class="mc__slide">...</div>
 *       <div class="mc__slide">...</div>
 *     </div>
 *   </mejoris-carousel>
 *
 * Requiere al menos 2 slides. Las imágenes dentro de cada slide deben llevar
 * loading="lazy" (el componente no gestiona la carga de imágenes, delega en
 * el navegador — así no duplicamos lógica que la plataforma ya resuelve bien).
 *
 * Atributos data-* (todos opcionales):
 *   data-autoplay       "true" | "false"  (default: true)
 *   data-speed          px/segundo de desplazamiento continuo (default: 36)
 *   data-loop           "true" | "false"  (default: true)
 *   data-resume-delay   ms antes de reanudar autoplay tras soltar el drag (default: 2500)
 *
 * Variables CSS que acepta (ver mejoris-carousel.css):
 *   --mc-gap, --mc-slide-width, --mc-slide-width-tablet, --mc-slide-width-desktop
 *
 * Comportamiento: movimiento continuo por transform (GPU, sin reflow), loop
 * infinito sin salto visible (clona el set de slides una vez), drag/swipe vía
 * Pointer Events (funciona igual en touch y mouse), se pausa fuera de viewport
 * (IntersectionObserver) y respeta prefers-reduced-motion. Cero dependencias.
 */
(function () {
  if (customElements.get('mejoris-carousel')) return;

  class MejorisCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.mc__track');
      if (!this.track) return;

      this.originalSlides = Array.prototype.slice.call(this.track.children);
      if (this.originalSlides.length < 2) return;

      this.autoplay = this.dataset.autoplay !== 'false';
      this.speed = parseFloat(this.dataset.speed) || 36;
      this.loop = this.dataset.loop !== 'false';
      this.resumeDelay = parseInt(this.dataset.resumeDelay, 10) || 2500;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.x = 0;
      this.setWidth = 0;
      this.dragging = false;
      this.dragStartX = 0;
      this.dragStartTranslate = 0;
      this.resumeTimer = null;
      this.visible = false;
      this.lastFrame = null;
      this.rafId = null;

      this.setAttribute('role', 'region');
      this.setAttribute('aria-roledescription', 'carousel');

      if (this.loop) this._cloneSlides();
      this._measure();
      this._bindEvents();
      this._observeVisibility();

      var self = this;
      this.resizeObserver = new ResizeObserver(function () { self._measure(); });
      this.resizeObserver.observe(this.track);

      if (this.autoplay && !this.reducedMotion) this._play();
    }

    disconnectedCallback() {
      this._pause();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.io) this.io.disconnect();
      clearTimeout(this.resumeTimer);
    }

    _cloneSlides() {
      var frag = document.createDocumentFragment();
      this.originalSlides.forEach(function (slide) {
        var clone = slide.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button, input, textarea, select').forEach(function (el) {
          el.tabIndex = -1;
        });
        frag.appendChild(clone);
      });
      this.track.appendChild(frag);
    }

    _measure() {
      var slides = Array.prototype.slice.call(this.track.children);
      var half = Math.ceil(slides.length / (this.loop ? 2 : 1));
      var first = slides[0];
      var last = slides[half - 1];
      if (!first || !last) return;
      this.setWidth = (last.offsetLeft + last.offsetWidth) - first.offsetLeft;
    }

    _bindEvents() {
      this.addEventListener('pointerdown', this._onPointerDown.bind(this));
      this.addEventListener('pointermove', this._onPointerMove.bind(this));
      this.addEventListener('pointerup', this._onPointerUp.bind(this));
      this.addEventListener('pointercancel', this._onPointerUp.bind(this));
      this.addEventListener('mouseenter', this._pause.bind(this));
      this.addEventListener('mouseleave', this._maybeResume.bind(this));
      this.addEventListener('focusin', this._pause.bind(this));
      this.addEventListener('focusout', this._maybeResume.bind(this));
    }

    _observeVisibility() {
      var self = this;
      this.io = new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
        if (self.visible && self.autoplay && !self.dragging && !self.reducedMotion) self._play();
        if (!self.visible) self._pause();
      }, { threshold: 0.1 });
      this.io.observe(this);
    }

    _play() {
      if (this.rafId) return;
      this.lastFrame = null;
      this.rafId = requestAnimationFrame(this._tick.bind(this));
    }

    _pause() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    _maybeResume() {
      var self = this;
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(function () {
        if (self.visible && self.autoplay && !self.dragging && !self.reducedMotion) self._play();
      }, this.resumeDelay);
    }

    _tick(now) {
      if (this.lastFrame == null) this.lastFrame = now;
      var dt = (now - this.lastFrame) / 1000;
      this.lastFrame = now;

      this.x -= this.speed * dt;
      this._wrap();
      this._render();

      this.rafId = requestAnimationFrame(this._tick.bind(this));
    }

    _wrap() {
      if (!this.loop || !this.setWidth) return;
      if (this.x <= -this.setWidth) this.x += this.setWidth;
      if (this.x > 0) this.x -= this.setWidth;
    }

    _render() {
      this.track.style.transform = 'translate3d(' + this.x + 'px,0,0)';
    }

    _onPointerDown(e) {
      this.dragging = true;
      this._pause();
      clearTimeout(this.resumeTimer);
      this.dragStartX = e.clientX;
      this.dragStartTranslate = this.x;
      if (this.setPointerCapture) {
        try { this.setPointerCapture(e.pointerId); } catch (err) {}
      }
    }

    _onPointerMove(e) {
      if (!this.dragging) return;
      var delta = e.clientX - this.dragStartX;
      this.x = this.dragStartTranslate + delta;
      this._wrap();
      this._render();
    }

    _onPointerUp() {
      if (!this.dragging) return;
      this.dragging = false;
      this._maybeResume();
    }
  }

  customElements.define('mejoris-carousel', MejorisCarousel);
})();
